/**
 * 🎒 The pack, open — and the first screen in this game you can *change* things
 * with (T152).
 *
 * Everything before this was the server telling the client what it holds. Here
 * the client asks the server to rearrange it, which is why the interesting part
 * of this file is not the drawing but the four rules under "picking something
 * up" below.
 *
 * ## 🔴 Nothing here decides what anything looks like
 *
 * The marks come from `hud-parts.ts` and the arrangement from `hud-scene.ts` —
 * the same two files the approved sheets in `Design/samples/` are drawn from.
 * This file is the plumbing: where the element sits, when it is rebuilt, and
 * what a click means. ⚠️ **The slot the mouse is over is worked out by
 * `packSlotAt`, which reads the same layout the drawing does**, so a click
 * cannot land one slot away from where the eye says it should.
 *
 * ## ⚠️ Nothing is carried by the SERVER
 *
 * An item picked up here never leaves its slot as far as the server is
 * concerned — the carrying is a drawing this file does, and only the finished
 * move is sent (`move_item`, protocol 10). So closing the pack mid-carry, or
 * losing the connection, puts nothing at risk: there was nothing in flight.
 * `packages/shared/src/protocol.ts` carries the full argument.
 */

import {
  INVENTORY_CARRIED,
  INVENTORY_ON_HAND,
  INVENTORY_SLOTS,
  canMake,
  recipeCategoriesAt,
  recipesInTab,
  type RecipeCategory,
  recipesShownAt,
  pictureItemFor,
  itemById,
  itemIdForKey,
  blockById,
  blockIdForKey,
  t,
  type MessageKey,
  itemIconUri,
  type Recipe,
  type Stack,
} from '@lofi/shared';
import { blockIconUri } from './blockicons.ts';
import { hudScale, makeScene, type VariantName } from './hud-scene.ts';
import { slotContentFor } from './hud-cluster.ts';
import type { SlotContent } from './hud-parts.ts';

/**
 * 🔨 **The word a SENTENCE uses for a tag or a station** — T130 slice 3.
 *
 * ⚠️ **Hand-written on purpose.** See {@link PackPanel.nameOf}: `t()` refuses a
 * key it cannot see at compile time, so a template string does not compile —
 * which is the guard working. A missing key would otherwise reach the player as
 * the raw text `craft.tag.grjot`. 👉 **Adding a tag to a recipe without adding
 * its word here fails the build**, which is exactly when somebody should be made
 * to think about the word.
 */
const CRAFT_WORDS: Readonly<Record<string, MessageKey>> = {
  vidur: 'craft.tag.vidur',
  grjot: 'craft.tag.grjot',
  /* 🎯 haugur, 2026-08-27, asked whether "Needs a Smíðabekkur" should stay:
     *"workbench is fine."* 👉 So a STATION named inside an English sentence
     gets an English word, while the item keeps its Icelandic name wherever it
     is named as a thing — which is what the pack has always done. */
  smidabekkur: 'craft.word.smidabekkur',
};

/** 🔨 And what each station is called, for the line under the tabs. */
const STATION_WORDS: Readonly<Record<string, MessageKey>> = {
  smidabekkur: 'craft.station.smidabekkur',
};

/** ✅ The variation haugur chose — the same one the HUD cluster draws in. */
const VARIANT: VariantName = 'scrim';

/** What the player has picked up and is holding over the panel. */
interface Carried {
  /** The slot it came out of, and the slot it goes back to if this is cancelled. */
  readonly from: number;
  readonly itemKey: string;
  readonly count: number;
  /**
   * 🔨 **How many blocks this tool has already broken**, carried along with it.
   *
   * 🔴 **Without this the wear bar BLINKS OFF while you drag a tool** and comes
   * back when you drop it. ⚠️ **Nothing would fail** — the number is right in
   * the pack and right in the chest and simply absent for the second in
   * between, which reads as a rendering fault rather than a missing field.
   * *Two ways in that must not differ, and this is the second one.*
   */
  readonly used?: number;
}

/** Asked of the server when a carried stack is put down somewhere. */
export type MoveRequest = (from: number, to: number, count: number) => void;

/**
 * 🗑️ Asked of the server when a carried stack is thrown away (T154).
 *
 * ⚠️ **Separate from {@link MoveRequest} on purpose.** One of these two cannot
 * be undone, and the caller should have to write a different word for it.
 */
export type DiscardRequest = (from: number, count: number, itemKey: string) => void;

/**
 * 🔨 Asked of the server when a recipe row is clicked — T130 slice 3, D95.
 *
 * ⚠️ **The recipe key and the station's position, and nothing else.** The
 * server looks up what that costs and whether the player has it; a client that
 * could name its own inputs could name four stones it does not own.
 */
export type CraftRequest = (recipe: string, at: { x: number; y: number; z: number } | null) => void;

/**
 * 🖱️ **Called whenever the panel opens or closes, so the MOUSE can change hands.**
 *
 * 🔴 **This exists because a second way in forgot to do it.** While you are
 * playing, the pointer is *locked* — the cursor is hidden and the mouse turns
 * your head. A panel is useless in that state: there is nothing to click a slot
 * with. `main.ts` released the lock in `setPackOpen`, and **`openAtStation` —
 * right-clicking a smíðabekkur — never went through it**, so the bench screen
 * opened with no cursor and you had to press **Escape** before you could touch
 * it. ⚠️ **The wheel was captured too**, so scrolling the recipe list stepped
 * along your hand row instead.
 *
 * 🎯 **haugur, 2026-09-02:** *"maybe we need to make it so that after you have
 * opened a menu, any menu, the mouse is automatically broken free."*
 *
 * 👉 **So the panel announces its own state and `main.ts` answers, once.** A
 * third way to open this screen cannot forget, because it is not a list of call
 * sites any more — it is the state changing. **The same argument that moved
 * `OPERATED_BLOCKS` into `block-behaviour.json` after it forgot the hearth and
 * then the driftwood.**
 */
export type OpenChanged = (open: boolean) => void;

/**
 * 🔨 **Ask the server what the bench can see from here** — T172, D111,
 * protocol 25.
 *
 * ⚠️ **It carries nothing.** The server answers from where it thinks the
 * player is, which is the position a craft is judged from — so the screen and
 * the authority cannot give different answers.
 */
export type AskNearbyRequest = () => void;

export class PackPanel {
  private readonly svg: SVGSVGElement;
  /** The panel: the scrim, the slots, the rule, the hand row. */
  private readonly body: SVGGElement;
  /** What is riding the cursor. Its own group so it can move without a redraw. */
  private readonly cursor: SVGGElement;

  private readonly onMove: MoveRequest;
  private readonly onDiscard: DiscardRequest;
  private readonly onCraft: CraftRequest;
  private readonly onAskNearby: AskNearbyRequest;
  /** 🖱️ Told whenever the panel opens or closes — see {@link OpenChanged}. */
  private readonly onOpenChanged: OpenChanged;

  /**
   * The panel's size **in the units the drawing is written in**, which on a big
   * screen is smaller than the element is (T170).
   *
   * 🔍 See {@link scale}: the element is `rawW` × `rawH` real pixels and the
   * drawing is laid out in `width` × `height`, so everything comes out
   * {@link scale} times bigger on the glass.
   */
  private width = 0;
  private height = 0;
  /** What the element actually measures, kept only to notice when it changes. */
  private rawW = 0;
  private rawH = 0;
  /**
   * 🔍 **How much bigger than the design frame this screen draws** — T170.
   *
   * ⚠️ **Every mouse position has to be divided by it.** The pointer arrives in
   * real pixels and `packSlotAt` answers in design units, and a click that
   * skipped this would land nearer the top-left the further out it was — which
   * looks like a hit-testing fault and is not one.
   */
  private scale = 1;
  private open = false;
  private stacks: readonly Stack[] = [];
  private selected = 0;
  private hover: number | null = null;
  private carried: Carried | null = null;
  /** 🔨 Which page is showing. The pack is the one you open with `E`. */
  private page: 'pack' | 'craft' = 'pack';
  /**
   * 🔨 The station being worked at, and where it is.
   *
   * ⚠️ **`null` is your own hands, and it is a real state** — the bench recipe
   * is `station: null` on purpose, or nothing could ever be made.
   */
  private station: string | null = null;
  private stationAt: { x: number; y: number; z: number } | null = null;
  /** The recipe row the mouse is over, on the crafting page. */
  private craftHover: number | null = null;
  /**
   * 🎡 **How far down the recipe list you have scrolled**, as the index of the
   * first row drawn.
   *
   * 🔴 **The list is longer than the panel and has been since the six tools
   * landed.** The bench shows nine recipes and there is room for five, and
   * until 2026-09-02 the rest were simply not drawn and nothing said so — 🎯
   * haugur went looking for the chest recipe he had just been handed and it was
   * not on the screen. **steinöxi, steinhaki and steinskófla had been missing
   * the same way for a day before that.**
   *
   * ⚠️ **Reset whenever the list underneath it changes** — closing the panel,
   * or walking up to a different station. A scroll position kept across a
   * change is a screen showing row six of a list that now has four.
   */
  private craftScroll = 0;
  /**
   * 🔨 **What the chests and storehouses around you hold** — T172, D111,
   * protocol 25.
   *
   * 🎯 **The bench has spent out of any container within 100 m since
   * 2026-09-04**, and this screen went on greying rows by the pack alone: *you
   * need 2 more stone*, with the stone in a chest behind you.
   *
   * 🔴 **DISPLAY ONLY.** The server recomputes every input when the craft
   * arrives, so this list going stale can cause a refusal and can never cause a
   * craft that spends something that is not there. **That is why the working
   * half could ship a session before the screen did.**
   *
   * 🔴 **THE SLOT NUMBERS ARE NEGATIVE AND THAT IS DELIBERATE.** These are
   * pooled counts with no slots at all; `canMake` sums by item key and never
   * looks at one. A negative slot cannot be mistaken for a real one by anything
   * that moves an item — **a pack's slots 34–37 are clothing and a búr's are
   * ordinary storage**, so a merged list is sound for counting and for nothing
   * else. ⚠️ **Never hand this to `moveStack`, and never draw it as a slot.**
   */
  /**
   * 🗂️ **Which craft tab is open** — D122, T249. An index into
   * {@link tabsHere}, clamped every time it is read, because the list of tabs
   * changes with the station.
   */
  private craftTab = 0;
  private nearby: readonly Stack[] = [];
  /** How many containers those came out of. ⚠️ Zero is a real answer. */
  private nearbyContainers = 0;

  constructor(
    onMove: MoveRequest,
    onDiscard: DiscardRequest,
    onCraft: CraftRequest,
    onOpenChanged: OpenChanged = () => {},
    /**
     * 🔨 **Ask the server what the bench can see** — T172.
     *
     * 🔴 **Called from `openAtStation`, not from `main.ts`'s two call sites.**
     * There are two ways to open a station — a right-click and the `O` key —
     * and *"a second way in forgets what the first one does"* is a fault this
     * repo has recorded four times, most recently on these very houses.
     */
    onAskNearby: AskNearbyRequest = () => {},
  ) {
    this.onMove = onMove;
    this.onDiscard = onDiscard;
    this.onCraft = onCraft;
    this.onOpenChanged = onOpenChanged;
    this.onAskNearby = onAskNearby;

    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('class', 'packpanel');
    this.svg.setAttribute('aria-hidden', 'true');
    this.svg.style.display = 'none';

    this.body = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.cursor = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.svg.append(this.body, this.cursor);
    document.body.appendChild(this.svg);

    /* 🔴 A ResizeObserver, never a window `resize` listener — the fault that put
       the whole HUD on screen at 4.27× on 2026-08-18. An `<svg>` measured
       before layout still has its default 300 × 150, a `resize` event never
       fires because the window never resized, and nothing throws. */
    new ResizeObserver(() => {
      this.measure();
    }).observe(this.svg);
    this.measure();

    this.svg.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.svg.addEventListener('mousedown', (e) => this.onMouseDown(e));
    /* Right-click takes half a stack, so the browser's own menu is in the way. */
    this.svg.addEventListener('contextmenu', (e) => e.preventDefault());

    /**
     * 🎡 **The wheel scrolls the recipe list.**
     *
     * ✅ **Nothing had to be taken away from the wheel to do this.** It steps
     * along the on-hand row while you are playing, but `controls.ts` only does
     * that when the pointer is LOCKED — and a panel being open is exactly when
     * it is not. Its own comment already said the event is let through *"so a
     * panel can scroll normally"*, and this is the panel that wanted it.
     *
     * ⚠️ **`passive: false` and `preventDefault`**, or the page scrolls under
     * the game as well as the list moving.
     */
    this.svg.addEventListener(
      'wheel',
      (e) => {
        if (this.page !== 'craft') return;
        const max = this.scene().craftMaxScroll(this.visibleRecipes().length);
        if (max === 0) return;
        e.preventDefault();
        const next = Math.min(Math.max(0, this.craftScroll + Math.sign(e.deltaY)), max);
        if (next === this.craftScroll) return;
        this.craftScroll = next;
        /* The row under the mouse has changed without the mouse moving. */
        const { x, y } = this.pointAt(e);
        this.craftHover = this.scene().craftRowAt(
          x,
          y,
          this.visibleRecipes().length,
          this.craftScroll,
        );
        this.drawBody();
      },
      { passive: false },
    );
  }

  /**
   * 🔨 **Open straight onto the crafting page, standing at a station.**
   *
   * ⚠️ **The client opening this screen is NOT permission to craft.** The
   * server re-reads the block at these coordinates and decides for itself —
   * the same two checks `use_block` makes.
   */
  openAtStation(station: string, at: { x: number; y: number; z: number }): void {
    this.station = station;
    this.stationAt = { ...at };
    this.page = 'craft';
    /* 🎡 A new station is a new list, so the scroll starts at the top.
       🗂️ And a new set of TABS — D122 — so the open one goes back to the
       first: a bench's third tab is not a hand's third tab, and keeping the
       index would land you on whichever one happened to be there. */
    this.craftScroll = 0;
    this.craftTab = 0;
    /**
     * 🔴 **Forget what the last bench could see, then ask.**
     *
     * ⚠️ **Keeping it would be the lying screen again**, one bench along: the
     * reach is 100 m and a player who walks to another farmstead would be shown
     * rows lit by chests they have left behind. **Until the answer arrives the
     * list counts the pack alone**, which is exactly what shipped on 2026-09-04
     * — so the worst moment of this screen is now its old best.
     */
    this.nearby = [];
    this.nearbyContainers = 0;
    this.onAskNearby();
    this.setOpen(true);
    this.draw();
  }

  /**
   * 🔨 **What the containers in reach hold, straight off the wire** — T172.
   *
   * ⚠️ **Ignored unless the craft page is showing.** An answer that arrives
   * after the player has walked away describes somewhere they are not.
   */
  setNearbyMaterials(
    stacks: readonly { readonly itemKey: string; readonly count: number }[],
    containers: number,
  ): void {
    if (this.page !== 'craft') return;
    this.nearbyContainers = containers;
    /* 🔴 Negative slots: see `nearby`. Not a slot, and unmistakable for one. */
    this.nearby = stacks.map((s, i) => ({ slot: -1 - i, itemKey: s.itemKey, count: s.count }));
    if (this.open) this.drawBody();
  }

  /**
   * 🔨 **Everything the bench can count** — the pack, and every container in
   * reach.
   *
   * ⚠️ **For `canMake` and nothing else.** It sums by item key; anything that
   * reads a slot must be given `this.stacks`.
   */
  private countableStacks(): readonly Stack[] {
    return this.nearby.length === 0 ? this.stacks : [...this.stacks, ...this.nearby];
  }

  /** Is the pack on screen? */
  get isOpen(): boolean {
    return this.open;
  }

  /**
   * 🔨 **Is the crafting list what the player is looking at?** — T172.
   *
   * ⚠️ Asked by `main.ts` so that a craft, which is the one thing that
   * certainly emptied a container, refreshes what the bench can see — and so
   * that an ordinary pickup while walking about does not.
   */
  get isShowingCraft(): boolean {
    return this.open && this.page === 'craft';
  }

  /**
   * Open or close the pack.
   *
   * ⚠️ **Closing puts down whatever was being carried**, back where it came
   * from. Nothing had been sent, so there is nothing to undo — but a stack that
   * quietly stayed picked up would come back on the cursor next time the panel
   * opened, which reads as an item that moved by itself.
   */
  setOpen(open: boolean): void {
    if (open === this.open) return;
    this.open = open;
    if (!open) {
      this.carried = null;
      this.hover = null;
      /* 🔴 Closing forgets the bench. Re-opening with `E` is standing in your
         own two hands again — the alternative is a screen that quietly claims
         you are at a bench you have walked away from. */
      this.page = 'pack';
      this.station = null;
      this.stationAt = null;
      this.craftHover = null;
      this.craftScroll = 0;
      /* 🔨 And what the bench could see. Re-opening asks again — T172. */
      this.nearby = [];
      this.nearbyContainers = 0;
    }
    this.svg.style.display = open ? 'block' : 'none';
    this.draw();
    /* 🖱️ Last, and after the drawing, so whoever hands the mouse over is
       looking at a panel that is already on the screen. */
    this.onOpenChanged(open);
  }

  /**
   * 🎒 What the player is carrying, straight off the wire.
   *
   * 🔴 **Any change from the server cancels a carry.** The stack riding the
   * cursor is a picture of what *was* in a slot; once the server says something
   * different, that picture is out of date and putting it down would ask for a
   * move against an inventory that no longer exists. The server would refuse it
   * — {@link moveStack} does not clamp — but the player would have watched a
   * drag do nothing, with no way to know why.
   */
  setInventory(stacks: readonly Stack[], selected: number): void {
    this.stacks = stacks;
    this.selected = selected;
    this.carried = null;
    this.draw();
  }

  /**
   * Where the mouse is, in the panel's own coordinates.
   *
   * The element is `position: fixed` under the status bar, so the element's own
   * offset has to come off the pointer.
   *
   * 🔴 **And then the zoom, which is the part that is easy to forget** (T170).
   * One unit of this drawing used to be one pixel on screen; since the HUD
   * scales itself to the screen it is {@link scale} pixels. ⚠️ **Without the
   * division a click lands short of where the eye says**, by more the further
   * from the top-left corner it is — at 1.75× the last slot on the hand row
   * misses by most of a slot. The drawing would be perfect and the game would
   * feel broken, which is the failure this file's opening comment is about.
   */
  private pointAt(e: MouseEvent): { x: number; y: number } {
    const box = this.svg.getBoundingClientRect();
    return { x: (e.clientX - box.left) / this.scale, y: (e.clientY - box.top) / this.scale };
  }

  private onMouseMove(e: MouseEvent): void {
    const { x, y } = this.pointAt(e);

    /* The carried stack follows the cursor every pixel — but by moving a group
       that is already drawn, never by redrawing it. */
    if (this.carried) this.cursor.setAttribute('transform', `translate(${String(x)},${String(y)})`);

    /* ⚠️ The panel is only rebuilt when the slot under the mouse CHANGES, not
       on every pixel of travel. A redraw is a parse and a subtree swap of 34
       slots; at mouse-move rates that is the fault `hud-cluster.ts` warns
       about, one screen along. */
    if (this.page === 'craft') {
      const row = this.scene().craftRowAt(x, y, this.visibleRecipes().length, this.craftScroll);
      if (row === this.craftHover) return;
      this.craftHover = row;
      this.drawBody();
      return;
    }

    const over = this.scene().packSlotAt(x, y);
    if (over === this.hover) return;
    this.hover = over;
    this.drawBody();
  }

  /**
   * 🔴 **The four rules of moving something, and they are the whole feature.**
   *
   * | | |
   * |---|---|
   * | **Click a slot with something in it** | Pick the whole stack up |
   * | **Right-click it** | Pick up **half**, rounded up — this is how a stack splits |
   * | **Click another slot while carrying** | Put it there. **This is the only thing that sends a message** |
   * | **Click the same slot, or off the panel** | Put it back. Nothing was ever sent |
   *
   * ⚠️ **Whether the move is allowed is not decided here.** A full destination,
   * a swap of half a stack onto a different item — those are the server's
   * answers, and it replies with the whole inventory either way. This file may
   * not pre-judge them, because two copies of one rule is how the screen and
   * the world start disagreeing.
   */
  private onMouseDown(e: MouseEvent): void {
    if (!this.open) return;
    /* Only the two buttons that mean something. The middle button and the
       browser's back/forward buttons pass through untouched. */
    if (e.button !== 0 && e.button !== 2) return;
    e.preventDefault();

    const { x, y } = this.pointAt(e);
    /* 🔨 The tabs come first on both pages — T130 slice 3.
       ⚠️ **A tab click while carrying something is ignored**, not silently
       dropped: switching pages with a stack on the cursor would leave it
       nowhere, and the pack's own rule is that closing puts it back. */
    const tab = this.scene().tabAt(x, y, {
      title: t('pack.title'),
      tabCraft: t('craft.tab.craft'),
    });
    if (tab !== null && this.carried === null) {
      if (tab !== this.page) {
        this.page = tab;
        this.craftHover = null;
        this.hover = null;
        this.draw();
      }
      return;
    }

    if (this.page === 'craft') {
      if (this.carried !== null) return;

      /* 🗂️ The category tabs come before the rows — D122. ⚠️ Before, because
         the tab row sits above the list and a click that fell through to
         `craftRowAt` would land on a recipe. */
      const tab = this.scene().craftTabAt(x, y, this.tabWords());
      if (tab !== null) {
        if (tab !== this.activeTab()) {
          this.craftTab = tab;
          /* 🎡 A new tab is a new list, so the scroll starts at the top — the
             same rule `openAtStation` follows for a new bench. */
          this.craftScroll = 0;
          this.craftHover = null;
          this.drawBody();
        }
        return;
      }

      const recipes = this.visibleRecipes();

      /* 🎡 The "▼ 2 more below" line is a button too — a page at a time. */
      const cue = this.scene().craftCueAt(x, y, recipes.length, this.craftScroll);
      if (cue !== null) {
        const S = this.scene();
        const max = S.craftMaxScroll(recipes.length);
        this.craftScroll = Math.min(Math.max(0, this.craftScroll + cue * S.craftRowsFit()), max);
        this.craftHover = null;
        this.drawBody();
        return;
      }

      const row = this.scene().craftRowAt(x, y, recipes.length, this.craftScroll);
      if (row === null) return;
      /* eslint-disable-next-line security/detect-object-injection --
         `row` came from `craftRowAt`, which bounds it against this same
         array length, and the `undefined` check below is the belt to that
         brace. The rule is about prototype pollution through attacker-named
         keys; this is an integer index into a local array. */
      const recipe = recipes[row];
      if (recipe === undefined) return;
      /* 🔴 One per click — D95. And the CLIENT does not check whether it can be
         made: it asks, and the server answers. A greyed row still sends, so a
         refusal always comes back with a reason rather than being a dead
         click. */
      this.onCraft(recipe.key, this.stationAt);
      return;
    }

    const slot = this.scene().packSlotAt(x, y);

    if (this.carried === null) {
      if (slot === null) return;
      const stack = this.stacks.find((s) => s.slot === slot);
      if (stack === undefined) return;
      /* Half, rounded UP — so right-clicking a single item picks it up rather
         than picking up nothing, which looks like a dead click. */
      const count = e.button === 2 ? Math.ceil(stack.count / 2) : stack.count;
      this.carried = {
        from: slot,
        itemKey: stack.itemKey,
        count,
        /* ⚠️ Spread in rather than assigned: `exactOptionalPropertyTypes` is on,
           so an explicit `undefined` is a different thing from an absent key. */
        ...(stack.used === undefined ? {} : { used: stack.used }),
      };
      this.cursor.setAttribute('transform', `translate(${String(x)},${String(y)})`);
      this.draw();
      return;
    }

    /* 🗑️ The bin, before anything else — it is not a slot, so `packSlotAt`
       does not know about it (T154).

       🔴 **Two deliberate actions and no undo.** You have already picked the
       stack up; this is the second. The mark sits about 260 px from the nearest
       slot precisely so it cannot be reached by a mis-click, and the game says
       what it threw away rather than doing it in silence. */
    if (this.scene().packBinAt(x, y)) {
      const { from, count, itemKey } = this.carried;
      this.carried = null;
      this.draw();
      this.onDiscard(from, count, itemKey);
      return;
    }

    /* Putting it down. Off the panel or back on its own slot is a cancel, and
       neither one sends anything. */
    if (slot === null || slot === this.carried.from) {
      this.carried = null;
      this.draw();
      return;
    }

    const { from, count } = this.carried;
    /* 🔴 The carry ends HERE, not when the answer comes back. The screen
       redraws from the server's next `inventory` message, which is the same
       single source of truth the number keys already work from — so a refused
       move puts everything back exactly as it was, and an accepted one arrives
       once. ⚠️ It can take a moment while the world is streaming (T151). */
    this.carried = null;
    this.draw();
    this.onMove(from, slot, count);
  }

  /**
   * Re-read the element's own size.
   *
   * ⚠️ **`clientWidth` is 0 until the element has been laid out**, and a panel
   * built at zero is drawn off the screen entirely.
   */
  private measure(): void {
    const w = this.svg.clientWidth;
    const h = this.svg.clientHeight;
    if (w === this.rawW && h === this.rawH) return;
    if (w <= 0 || h <= 0) return;
    this.rawW = w;
    this.rawH = h;
    /* 🔍 T170 — the zoom, and the whole of it. The drawing is laid out as if
       the screen were this many units across, and the viewBox stretches those
       units back over the real element, so slots, icons, spacing and text all
       grow together. ⚠️ Nothing in `hud-scene.ts` knows this happened. */
    this.scale = hudScale(w, h);
    this.width = w / this.scale;
    this.height = h / this.scale;
    this.svg.setAttribute('viewBox', `0 0 ${String(this.width)} ${String(this.height)}`);
    this.draw();
  }

  private scene(): ReturnType<typeof makeScene> {
    /* 🔴 `TOPBAR: 0`, and it is load-bearing. This element is already
       positioned below the status bar, so the bar covers none of it — the
       sample sheets are the ones that need the 34, because their SVG is the
       whole window. Getting this wrong left a band of undimmed world across the
       top of the screen and looked deliberate. */
    return makeScene({ SW: this.width, SH: this.height, TOPBAR: 0 });
  }

  private draw(): void {
    this.drawBody();
    this.drawCursor();
  }

  /**
   * The panel, with the carried items taken out of the slot they came from.
   *
   * 🔴 **The source slot must show the loss immediately.** It is the only thing
   * on screen saying the pick-up happened at all — without it a stack appears to
   * be in two places, which is exactly the "did that work?" moment this project
   * keeps paying for elsewhere.
   */
  private drawBody(): void {
    if (!this.open || this.width <= 0) return;
    if (this.page === 'craft') return this.drawCraft();

    const contents: SlotContent[] = Array.from({ length: INVENTORY_SLOTS }, () => ({}));
    for (const stack of this.stacks) {
      if (stack.slot < 0 || stack.slot >= INVENTORY_SLOTS) continue;
      const left =
        stack.slot === this.carried?.from ? stack.count - this.carried.count : stack.count;
      if (left <= 0) continue;
      contents[stack.slot] = slotContentFor({ ...stack, count: left });
    }

    const scene = this.scene();
    replace(
      this.body,
      scene.packPanel(
        VARIANT,
        /* 👕 Three slices, and the boundaries are the shared numbers rather
           than literals — the pack ends where the clothing begins. */
        contents.slice(INVENTORY_ON_HAND, INVENTORY_CARRIED),
        contents.slice(0, INVENTORY_ON_HAND),
        this.selected,
        this.hover,
        {
          title: t('pack.title'),
          close: t('pack.close'),
          onHand: t('pack.onHand'),
          onYou: t('pack.onYou'),
          garments: [t('pack.head'), t('pack.body'), t('pack.legs'), t('pack.feet')],
          tabCraft: t('craft.tab.craft'),
        },
        contents.slice(INVENTORY_CARRIED),
        this.carried !== null,
      ),
    );
  }

  /**
   * 🔨 **The recipes you could make, and what you are short of** — D95.
   *
   * 🔴 **Nothing here decides whether a recipe is possible.** `recipes.ts`
   * answers *can I make this* and *what am I lacking*; this turns the answer
   * into words and pictures. The same separation `slotContentFor` keeps.
   */
  /**
   * 🗂️ **The tabs this station has** — D122, derived from its own recipes so a
   * tab can never be empty. ⚠️ Asked in three places, so it is one function.
   */
  private tabsHere(): readonly RecipeCategory[] {
    return recipeCategoriesAt(this.station);
  }

  /**
   * 🗂️ **The tabs as words a player reads** — D122.
   *
   * 🔴 **An explicit map, and the type checker is why.** `t()` only accepts a
   * key it can see at compile time, so a template string is refused — **which
   * is the guard working**: a new category without a word here fails to
   * compile, rather than drawing the raw key `craft.tab.steinn` at somebody.
   * **The same arrangement `nameOf` already uses for the recipe tags.**
   */
  private tabWords(): readonly string[] {
    /* 🔴 A Map rather than a plain object, and it is the house pattern rather
       than a preference: indexing an object with a key that came from a data
       file is exactly what `security/detect-object-injection` exists to catch,
       and `blocks.ts` keeps a Map to ANSWER that rather than suppress it. */
    const WORDS = new Map<RecipeCategory, MessageKey>([
      ['efni', 'craft.tab.efni'],
      ['tre', 'craft.tab.tre'],
      ['steinn', 'craft.tab.steinn'],
      ['hus', 'craft.tab.hus'],
      ['heimili', 'craft.tab.heimili'],
      ['veidi', 'craft.tab.veidi'],
      ['bufe', 'craft.tab.bufe'],
    ]);
    /* ⚠️ `?? c` never fires — the Map is exhaustive over the union and the type
       checker says so. It is here because a tab drawn as nothing at all would
       be a tab nobody can click, which is this ticket wearing a new coat. */
    return this.tabsHere().map((cat) => {
      const key = WORDS.get(cat);
      return key === undefined ? cat : t(key);
    });
  }

  /** 🗂️ Which tab is lit, clamped to what this station actually has. */
  private activeTab(): number {
    const n = this.tabsHere().length;
    if (n === 0) return 0;
    return Math.min(Math.max(0, this.craftTab), n - 1);
  }

  /**
   * 🔨 **The recipes ON THE OPEN TAB** — D122.
   *
   * 🔴 **This used to be every recipe the station could make**, which on a
   * bench was **thirteen against five rows that fit** — so eight were invisible,
   * the spjót among them, and 🎯 haugur could not find the spear he had gone
   * there to make. ⚠️ **Two goes at a scroll indicator had already failed**; the
   * fix is that no tab is longer than the screen, and `hud-scene.test.ts`
   * asserts it so a fourteenth recipe cannot quietly hide the thirteenth.
   */
  private visibleRecipes(): readonly Recipe[] {
    /* 🔴 **The rule itself lives in `@lofi/shared`, and it moved there to be
       TESTED.** There is no `pack.test.ts` — this class needs a document — so
       for as long as the rule was a filter inside a private method, **haugur's
       decision about what a screen lists was the one part of it nothing
       checked.** `recipesShownAt` carries the whole argument and
       `recipes.test.ts` pins it.

       🔴 D95: a recipe you have not LEARNED should not be here at all. ⚠️
       **Nothing reads `unlockedBy` yet — that is slice 4** — so every reachable
       recipe is treated as known. That is also what makes the cost of showing
       only hand recipes real today: see `recipesShownAt`. */
    const tabs = this.tabsHere();
    const here = tabs.at(this.activeTab());
    return here === undefined ? recipesShownAt(this.station) : recipesInTab(this.station, here);
  }

  /**
   * One cell of the picture, as the real block face where there is one.
   *
   * 🔴 **A cell may be a TAG or an ITEM, and `tagItems` THROWS on a
   * non-tag.** That is right of `tagItems` — a missing tag is a typo of ours,
   * not something waiting to be built — but it means this has to ask first.
   *
   * ⚠️ **This threw on the live server on 2026-08-27** and the Craft tab did
   * nothing at all: the result of a recipe is an ITEM, `smidabekkur`, and it
   * was being looked up as a tag. **Nothing was visibly broken — the click
   * landed, the exception escaped, and the page simply never redrew.**
   *
   * 👉 **A tag stands for several items; the picture shows the first**, which
   * is the one a player is most likely to be holding.
   */
  private cellFor(key: string): SlotContent {
    const itemKey = pictureItemFor(key);
    const def = itemById(itemIdForKey(itemKey) ?? -1);

    /* 🪓 A photographed item wins — the same order the pack slots follow, so a
       tool looks the same in the Craft list as it does once you own it. */
    const photo = itemIconUri(itemKey);
    if (photo !== undefined) return { item: itemKey, photoUri: photo };

    const blockKey = def?.placesBlock ?? itemKey;
    const uri = blockIconUri(blockKey);
    if (uri !== undefined) return { item: itemKey, iconUri: uri };
    const colour = blockById(blockIdForKey(blockKey) ?? -1)?.colour;
    return colour === undefined ? { item: itemKey } : { item: itemKey, colour };
  }

  /**
   * What a tag or a station is called in a sentence a player reads.
   *
   * 🔴 **An explicit map, and the type checker is why.** `t()` only accepts a
   * key it can see at compile time, so a template string is refused — which is
   * the guard working: a missing key would otherwise reach the player as the
   * raw text `craft.tag.grjot`. 👉 **Adding a tag to a recipe without adding
   * its word here fails to compile**, which is exactly when somebody should be
   * made to think about it.
   *
   * ⚠️ Falls through to the ITEM own Icelandic name, because an input may name
   * a single item rather than a tag.
   */
  private nameOf(key: string): string {
    /* eslint-disable-next-line security/detect-object-injection --
       `TAG_WORDS` is a frozen literal of our own recipe tags and the lookup
       is read-only. A key that is not in it returns `undefined` and falls
       through to the item name below, which is the whole design. */
    const word = CRAFT_WORDS[key];
    if (word !== undefined) return t(word);
    return itemById(itemIdForKey(key) ?? -1)?.nameIs ?? key;
  }

  private drawCraft(): void {
    const scene = this.scene();
    const recipes = this.visibleRecipes();

    const rows = recipes.map((recipe) => {
      /* 🔨 **The pack AND every container in reach** — T172, D111. Until
         2026-09-07 this counted the pack alone while the bench spent out of
         chests, so a row said "you need 2 more stone" with the stone in a
         chest behind the player. ⚠️ Clicking it worked anyway, which is what
         let the two halves ship a session apart. */
      const check = canMake(recipe, this.countableStacks(), this.station);
      const resultKey = recipe.result.item;
      const short =
        check.reason === 'wrong-station'
          ? t('craft.needs', { station: this.nameOf(recipe.station ?? '') })
          : check.short
              .map((s) => t('craft.short', { count: String(s.by), of: this.nameOf(s.of) }))
              .join('  ·  ');
      return {
        result: {
          ...this.cellFor(resultKey),
          item: resultKey,
          count: recipe.result.count,
        },
        name: itemById(itemIdForKey(resultKey) ?? -1)?.nameIs ?? resultKey,
        picture: (recipe.arrangement ?? []).map((cell) =>
          cell === null ? null : this.cellFor(cell),
        ),
        cols: recipe.arrangementCols ?? 3,
        can: check.ok,
        short,
      };
    });

    replace(
      this.body,
      scene.craftPage(
        VARIANT,
        rows,
        this.craftHover,
        {
          title: t('pack.title'),
          close: t('pack.close'),
          empty: t('craft.empty'),
          tabCraft: t('craft.tab.craft'),
          /* 🎡 Left with `{count}` standing — the scene knows how many are hidden. */
          more: t('craft.more'),
          above: t('craft.above'),
        },
        this.station === null
          ? t('craft.station.hands')
          : t(STATION_WORDS[this.station] ?? 'craft.station.hands'),
        this.craftScroll,
        this.tabWords(),
        this.activeTab(),
      ),
    );
  }

  /**
   * What is riding the cursor.
   *
   * Drawn once when it is picked up, then *moved* by a transform — a stack that
   * followed the mouse by being redrawn would rebuild an icon sixty times a
   * second for a picture that has not changed.
   */
  private drawCursor(): void {
    if (this.carried === null || this.width <= 0) {
      replace(this.cursor, '');
      return;
    }
    const scene = this.scene();
    const half = scene.SLOT / 2;
    /* Centred on the pointer, and it does not take clicks — the slot underneath
       has to be what a click lands on. */
    replace(
      this.cursor,
      `<g transform="translate(${String(-half)},${String(-half)})" pointer-events="none">` +
        scene.P.slot('carved', 0, 0, slotContentFor(carriedAsStack(this.carried))) +
        `</g>`,
    );
  }
}

/** The carried items, shaped like the {@link Stack} the drawing already knows. */
function carriedAsStack(carried: Carried): Stack {
  return {
    slot: carried.from,
    itemKey: carried.itemKey,
    count: carried.count,
    ...(carried.used === undefined ? {} : { used: carried.used }),
  };
}

/**
 * Put a fragment of SVG markup inside an element.
 *
 * ⚠️ **Parsed, not assigned to `innerHTML`** — the same rule, and the same
 * reasoning, as `hud-cluster.ts`: everything drawn here is ours today, and the
 * moment an item name comes from somewhere else this is the line that would
 * have had to change.
 */
function replace(target: SVGGElement, markup: string): void {
  if (markup === '') {
    target.replaceChildren();
    return;
  }
  const parsed = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`,
    'image/svg+xml',
  );
  const root = parsed.documentElement;
  if (root.nodeName === 'parsererror' || root.getElementsByTagName('parsererror').length > 0) {
    console.error('Pack: could not parse the drawing', root.textContent);
    return;
  }
  target.replaceChildren(...Array.from(root.childNodes).map((n) => document.importNode(n, true)));
}
